/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * RequestResponseInspector (#867) — read-only inspector for the demand-source
 * driver "capture triple": the fully-resolved outbound request bidBrain sent
 * (URL + method + masked headers + resolved body) and the raw response received
 * (status + timing + raw payload). This is the single highest-leverage debug
 * aid for the ORTB pilot — it shows what was actually sent vs. what came back.
 *
 * The capture is produced by libs/drivers/driver-capture.ts (gated, test-path
 * only) and arrives on the quick-test result body as `capture`.
 */

import React from 'react';
import { Badge, Box, Button, Flex, HStack, Text } from '@chakra-ui/react';
import { PayloadViewer } from './PayloadViewer';
import { ProducedAdsView } from './ProducedAdsView';
import type { ParsedAd } from '../../utils/test-diagnosis';
import { detectPayloadLanguage } from './payload-language';

/** Fully-resolved outbound request as actually sent (auth headers masked). */
export interface CapturedRequest {
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: string;
  /**
   * Publisher values supplied on the inbound request that this outbound request did
   * not carry, because the driver builds those fields itself (#919 · D5).
   */
  customCollisions?: Array<{ key: string; path: string }>;
}

/** Raw response as received from the demand source, before parsing. */
export interface CapturedResponse {
  body: string;
  status: number;
  timingMs: number;
  /** Per-ad/per-bid parsed `response` roots the response mapping reads (#873). */
  parsed?: unknown[];
}

/** The request/response artifacts retained when driver capture is enabled. */
export interface DriverCapture {
  request: CapturedRequest;
  response?: CapturedResponse;
}

const KeyVal: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Flex gap={2} fontSize="xs" align="baseline">
    <Text color="fg.muted" minW="64px" fontWeight="600">
      {label}
    </Text>
    <Box color="fg.default" fontFamily="mono" wordBreak="break-all">
      {children}
    </Box>
  </Flex>
);

const HeadersTable: React.FC<{ headers: Record<string, string> }> = ({ headers }) => {
  const entries = Object.entries(headers);
  if (entries.length === 0) return null;
  return (
    <Box mt={2}>
      <Text fontSize="xs" fontWeight="600" color="fg.muted" mb={1}>
        Headers
      </Text>
      <Box borderRadius="6px" borderWidth="1px" borderColor="border.default" overflow="hidden">
        {entries.map(([name, value], i) => (
          <Flex
            key={name}
            fontSize="xs"
            fontFamily="mono"
            px={2}
            py={1}
            gap={2}
            bg={i % 2 === 0 ? 'bg.canvas' : 'transparent'}
          >
            <Text color="accent.solid" minW="140px" wordBreak="break-all">
              {name}
            </Text>
            <Text color="fg.default" wordBreak="break-all">
              {value}
            </Text>
          </Flex>
        ))}
      </Box>
    </Box>
  );
};

const Section: React.FC<{ title: string; accent: string; children: React.ReactNode }> = ({
  title,
  accent,
  children,
}) => (
  <Box>
    <Flex align="center" gap={2} mb={2}>
      <Box w="3px" h="14px" borderRadius="full" bg={accent} />
      <Text fontSize="xs" fontWeight="700" color="fg.title" textTransform="uppercase" letterSpacing="0.05em">
        {title}
      </Text>
    </Flex>
    {children}
  </Box>
);

/**
 * Response body viewer with Raw | Parsed tabs. The Parsed tab shows the per-ad/
 * per-bid `response` roots the response mapping reads (#873) — its paths match
 * `response.*` in the mapping editor 1:1. Only shown when a parse is available.
 */
const ResponseBodyView: React.FC<{ response: CapturedResponse }> = ({ response }) => {
  const parsed = Array.isArray(response.parsed) ? response.parsed : [];
  const hasParsed = parsed.length > 0;
  const [tab, setTab] = React.useState<'raw' | 'parsed'>('raw');
  const active = hasParsed ? tab : 'raw';

  if (!response.body && !hasParsed) {
    return (
      <Text fontSize="xs" color="fg.muted" fontStyle="italic">
        Empty response body.
      </Text>
    );
  }

  const parsedValue = JSON.stringify(parsed.length === 1 ? parsed[0] : parsed, null, 2);

  return (
    <Box>
      {hasParsed && (
        <HStack
          gap={1}
          mb={2}
          bg="bg.subtle"
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="md"
          p={1}
          width="fit-content"
        >
          {(['raw', 'parsed'] as const).map((t) => (
            <Button
              key={t}
              size="xs"
              variant="ghost"
              bg={active === t ? 'accent.subtle' : 'transparent'}
              color={active === t ? 'accent.solid' : 'fg.muted'}
              fontWeight={active === t ? '600' : '400'}
              _hover={{ bg: active === t ? 'accent.subtle' : 'bg.canvas' }}
              onClick={() => setTab(t)}
            >
              {t === 'raw' ? 'Raw' : `Parsed (${parsed.length})`}
            </Button>
          ))}
        </HStack>
      )}
      {active === 'parsed' ? (
        <PayloadViewer
          value={parsedValue}
          language="json"
          label="Parsed — the response.* roots your mapping reads"
          maxHeight="320px"
        />
      ) : (
        <PayloadViewer
          value={response.body}
          language={detectPayloadLanguage(response.body)}
          label="Raw response"
          maxHeight="320px"
        />
      )}
    </Box>
  );
};

/**
 * **The outbound request, as the driver built it** — the one rendering of it, for a request that was
 * sent and for one that was only resolved (`#979`'s *Preview request*). The title is the caller's
 * because the two are different claims: a preview must never read *Request sent*.
 */
export const CapturedRequestView: React.FC<{ request: CapturedRequest; title: string }> = ({ request, title }) => (
  <Section title={title} accent="accent.solid">
    <Flex direction="column" gap={1}>
      <KeyVal label="Method">{request.method}</KeyVal>
      <KeyVal label="URL">{request.url}</KeyVal>
    </Flex>
    <HeadersTable headers={request.headers} />
    {request.body && (
      <Box mt={2}>
        <PayloadViewer
          value={request.body}
          language={detectPayloadLanguage(request.body)}
          label="Body"
          maxHeight="240px"
        />
      </Box>
    )}

    {/* Custom values the request did NOT carry (#919 · D5). Procedural winning is
          correct; it being invisible is the same defect as the omitted-key macro
          (#880). This answers "why isn't my value in the request" in the place the
          question gets asked — next to the request itself. Not an error: a legitimate
          state, shown in a neutral-warning tone rather than a failure one. */}
    {request.customCollisions && request.customCollisions.length > 0 && (
      <Box mt={3} p={2} bg="warning.tint" borderRadius="6px">
        <Text fontSize="xs" fontWeight="600" color="warning.ink" mb={1}>
          {request.customCollisions.length} supplied value
          {request.customCollisions.length === 1 ? '' : 's'} not sent
        </Text>
        <Text fontSize="xs" color="warning.ink" mb={1}>
          These arrived on the inbound request. bidBrain builds these fields itself, so its own value was kept — a
          publisher value never overrides a demand source's configuration.
        </Text>
        <Flex direction="column" gap={0.5}>
          {request.customCollisions.map((c) => (
            <Text key={c.path} fontSize="xs" fontFamily="mono" color="warning.ink">
              {c.key} → {c.path}
            </Text>
          ))}
        </Flex>
      </Box>
    )}
  </Section>
);

export interface RequestResponseInspectorProps {
  capture: DriverCapture;
  /**
   * The ads the driver produced from that response — the side of the mapping the
   * operator maps **to**. Optional: a failure before parsing has none.
   *
   * Request, raw response and parsed roots are all the *from* side. Without this
   * the inspector showed everything available to map and nothing about whether
   * mapping worked, while the surface promised "what values are extracted back
   * into the session".
   */
  producedAds?: readonly ParsedAd[];
}

export const RequestResponseInspector: React.FC<RequestResponseInspectorProps> = ({ capture, producedAds }) => {
  const { request, response } = capture;

  return (
    <Flex direction="column" gap={4} mt={2}>
      <CapturedRequestView request={request} title="Request sent" />

      <Section title="Response received" accent={response && response.status < 400 ? 'success.solid' : 'error.solid'}>
        {response ? (
          <>
            <Flex align="center" gap={2} mb={2}>
              <Badge
                size="sm"
                bg={response.status < 400 ? 'success.tint' : 'error.tint'}
                color={response.status < 400 ? 'success.ink' : 'error.ink'}
              >
                HTTP {response.status}
              </Badge>
              <Text fontSize="xs" color="fg.muted">
                {response.timingMs} ms
              </Text>
            </Flex>
            <ResponseBodyView response={response} />
          </>
        ) : (
          <Text fontSize="xs" color="fg.muted" fontStyle="italic">
            No response was received (the request failed before completing).
          </Text>
        )}
      </Section>

      {/* The result. Request → response → what we made of it, read as one
          narrative: sent, received, produced. */}
      {producedAds && (
        <Section title="Ads produced" accent="accent.solid">
          <ProducedAdsView ads={producedAds} />
        </Section>
      )}
    </Flex>
  );
};
